'use client'
import { motion } from 'framer-motion'

interface FeatureItem {
  title: string
  description: string
  icon: React.ReactNode
  gradient?: string
}

interface FeatureCardGridProps {
  items: FeatureItem[]
  accent?: string
  accent2?: string
  layout?: 'stacked' | 'inline'
  gridClass?: string
  staggerDelay?: number
  cardClass?: string
}

function hexToRgb(hex: string) {
  const v = parseInt(hex.replace('#', ''), 16)
  return `${(v >> 16) & 255},${(v >> 8) & 255},${v & 255}`
}

export default function FeatureCardGrid({
  items,
  accent = '#5983e0',
  accent2 = '#4a6fc7',
  layout = 'stacked',
  gridClass = 'sm:grid-cols-2 lg:grid-cols-3 gap-5',
  staggerDelay = 0.08,
  cardClass = 'rounded-2xl',
}: FeatureCardGridProps) {
  const rgb = hexToRgb(accent)
  const borderHover = `${accent}4D`
  const id = `fcg-${accent.replace('#', '')}`

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .${id} > div:hover {
          border-color: ${borderHover} !important;
          box-shadow: 0 0 50px rgba(${rgb}, 0.12);
        }
        .${id} .fg-top-line {
          background: linear-gradient(to right, transparent, ${accent}66, transparent);
        }
      ` }} />
      <div className={`grid ${gridClass} ${id}`}>
        {items.map((item_, i) => (
          <motion.div
            key={item_.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5, delay: i * staggerDelay }}
            className={`group relative p-6 ${cardClass} bg-white/[0.015] border border-white/[0.06] transition-all duration-500`}
          >
            <div className="absolute inset-x-0 top-0 h-[1px] fg-top-line opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div
              className="absolute -top-24 -right-24 w-48 h-48 rounded-full blur-3xl transition-all duration-700 pointer-events-none"
              style={{
                background: `${accent}08`,
              }}
            />
            <div
              className="absolute -top-24 -right-24 w-48 h-48 rounded-full blur-3xl transition-all duration-700 pointer-events-none group-hover:opacity-100 opacity-0"
              style={{
                background: `${accent}`,
                opacity: 0.06,
              }}
            />

            <div className="relative z-10">
              {layout === 'inline' ? (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-lg shrink-0"
                      style={{
                        background: item_.gradient || `linear-gradient(to bottom right, ${accent}, ${accent2})`,
                      }}
                    >
                      {item_.icon}
                    </div>
                    <h3 className="text-[15px] font-semibold text-white leading-tight">{item_.title}</h3>
                  </div>
                  <p className="text-sm text-[#94a3b8] leading-relaxed">{item_.description}</p>
                </>
              ) : (
                <>
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg mb-4"
                    style={{
                      background: item_.gradient || `linear-gradient(to bottom right, ${accent}, ${accent2})`,
                    }}
                  >
                    {item_.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item_.title}</h3>
                  <p className="text-sm text-[#94a3b8] leading-relaxed">{item_.description}</p>
                </>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </>
  )
}
